import { Consentimento } from './types';

/**
 * O que o jogador consentiu, guardado no navegador dele.
 *
 * Location: src/lib/widgets
 * Why: **no navegador, não no nosso banco.** É preferência do jogador, e
 * guardar do nosso lado criaria um registro de rastreamento sobre justamente
 * quem recusou ser rastreado — o oposto do que a escolha significa.
 *
 * Por loja, porque o jogador pode aceitar numa e recusar noutra, e uma escolha
 * feita numa loja não autoriza outra a rastreá-lo.
 */

const PREFIXO = 'hystore.consent.';

/** Enquanto não houver escolha, nada de análise ou anúncio carrega. */
export const NEGADO: Consentimento = { analytics: false, advertising: false };

export const ACEITO_TUDO: Consentimento = { analytics: true, advertising: true };

function chave(storeId: string): string {
  return `${PREFIXO}${storeId}`;
}

export interface EstadoConsentimento {
  /** O jogador já respondeu? Se não, o banner aparece. */
  respondido: boolean;
  valor: Consentimento;
}

export function lerConsentimento(storeId: string): EstadoConsentimento {
  try {
    const bruto = localStorage.getItem(chave(storeId));

    if (!bruto) return { respondido: false, valor: NEGADO };

    const parsed = JSON.parse(bruto);

    /*
     * Só booleano vira consentimento.
     *
     * `localStorage` é editável pelo jogador e por qualquer script na página.
     * Um valor estranho — de uma versão antiga, ou adulterado — cai no negado,
     * que é o lado seguro: no máximo pergunta de novo.
     */
    return {
      respondido: true,
      valor: {
        analytics: parsed?.analytics === true,
        advertising: parsed?.advertising === true,
      },
    };
  } catch {
    /*
     * `localStorage` lança em janela anônima com dados bloqueados. Sem
     * armazenamento, a resposta é não rastrear — e perguntar de novo a cada
     * visita é o comportamento correto de quem não pode lembrar.
     */
    return { respondido: false, valor: NEGADO };
  }
}

/** Avisado a cada resposta do banner: o espaço de anúncio do Google espera o aceite. */
export const EVENTO_CONSENTIMENTO = 'hystore:consentimento';

export function gravarConsentimento(storeId: string, valor: Consentimento): void {
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_CONSENTIMENTO, { detail: { storeId, valor } }));
  } catch {
    // Sem CustomEvent (navegador muito antigo): o anúncio só lê o aceite na próxima página.
  }
  try {
    localStorage.setItem(chave(storeId), JSON.stringify(valor));
  } catch {
    /*
     * Não conseguir guardar não impede a escolha de valer nesta visita: o
     * estado em memória segue adiante. O jogador só será perguntado de novo.
     */
  }
}
